import { useState, type FormEvent } from "react";
import PageMeta from "@/components/seo/PageMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { SITE_URL, siteInfo } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahc3fjoh653ivfvnr1g";

const topicOptions = [
  "花絮现场",
  "路透速览",
  "侧拍角度",
  "彩蛋收集",
  "候场切片",
  "其他问题",
];

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "提交失败，请稍后重试。";

      const failed =
        !response.ok ||
        parsed?.code !== "OK" ||
        /spam/i.test(String(serverMsg));

      if (failed) {
        setStatus("error");
        setFormError(String(serverMsg));
        return;
      }

      setStatus("success");
      setMessageLength(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请稍后重试。");
    }
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 | 吃瓜片场",
    url: `${SITE_URL}/contact`,
    inLanguage: "zh-CN",
    description:
      "通过表单提交剧名、场景与线索说明，或联系我们补充私拍吃瓜花絮、路透、侧拍、彩蛋与候场相关内容。",
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: SITE_URL,
    email: siteInfo.email,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "读者与线索咨询",
      email: siteInfo.email,
      availableLanguage: ["zh-CN"],
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "联系我们",
        item: `${SITE_URL}/contact`,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="联系我们 | 吃瓜片场线索提交与咨询"
        description="想补充私拍吃瓜花絮、路透、侧拍、彩蛋或候场线索，或对内容有疑问，都可以通过本页表单与我们联系。写清作品名称、场景与时间点会更有帮助。"
        keywords="联系吃瓜片场,提交线索,私拍吃瓜咨询"
        path="/contact"
        jsonLd={[contactSchema, organizationSchema, breadcrumbSchema]}
      />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <Breadcrumbs items={[{ label: "联系我们" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5">
            联系我们
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4 max-w-3xl">
            无论你想补充一条现场线索，还是对分类与内容有疑问，都可以直接填写下面的表单。
            建议写清作品名称、场景描述与大致时间点，我们会更容易核对并给出回复。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                提交线索 / 留言
              </h2>
              <p className="text-sm text-foreground-600 mt-2">
                带 <span className="text-primary-600">*</span> 的为必填项。
              </p>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-7 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-name"
                      className="text-sm font-medium text-foreground-800"
                    >
                      你的称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      placeholder="怎么称呼你"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-email"
                      className="text-sm font-medium text-foreground-800"
                    >
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="用于接收回复"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-drama"
                      className="text-sm font-medium text-foreground-800"
                    >
                      作品名称
                    </label>
                    <input
                      id="contact-drama"
                      name="drama"
                      type="text"
                      placeholder="例如《雾港迷航》"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-topic"
                      className="text-sm font-medium text-foreground-800"
                    >
                      线索分类
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue={topicOptions[0]}
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors cursor-pointer"
                    >
                      {topicOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="contact-message"
                    className="text-sm font-medium text-foreground-800"
                  >
                    线索说明 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请写清场景、大致时间点与你想确认的细节，500 字以内。"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors resize-none"
                  />
                  <span className="text-xs text-foreground-500 self-end">
                    {messageLength} / 500
                  </span>
                </div>

                <div className="field-extra" aria-hidden="true">
                  <label htmlFor="contact-website-alt">请勿填写此栏</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {status === "submitting" ? "正在提交…" : "提交线索"}
                  </button>
                  <p className="text-xs text-foreground-500">
                    我们不会公开你的联系方式，仅用于回复本次咨询。
                  </p>
                </div>

                {status === "success" && (
                  <div className="flex items-start gap-3 px-4 py-3.5 rounded-md bg-secondary-100 border border-secondary-200">
                    <i
                      className="ri-checkbox-circle-line text-secondary-700 text-lg"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-secondary-900 leading-relaxed">
                      提交成功，感谢你的线索。我们会在 {siteInfo.replyTime}处理并回复到你的邮箱。
                    </p>
                  </div>
                )}

                {status === "error" && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 px-4 py-3.5 rounded-md bg-primary-50 border border-primary-200"
                  >
                    <i
                      className="ri-error-warning-line text-primary-700 text-lg"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-primary-800 leading-relaxed">
                      {formError || "提交失败，请检查必填项后重试。"}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="flex flex-col gap-6">
              <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">
                  直接联系
                </h2>
                <ul className="flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-900">邮箱</span>
                      <span className="text-sm text-foreground-600 mt-0.5">
                        {siteInfo.email}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
                      <i className="ri-wechat-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-900">
                        微信公众号
                      </span>
                      <span className="text-sm text-foreground-600 mt-0.5">
                        {siteInfo.wechat}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                      <i className="ri-time-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-900">
                        回复时间
                      </span>
                      <span className="text-sm text-foreground-600 mt-0.5">
                        {siteInfo.replyTime}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-secondary-950 text-secondary-100 rounded-lg p-6">
                <h2 className="font-heading text-lg font-bold text-background-50 mb-4">
                  怎样写更容易被采用
                </h2>
                <ul className="flex flex-col gap-3">
                  {[
                    "写清作品名称与大致拍摄阶段",
                    "描述具体场景，而不是笼统感受",
                    "说明你想确认或补充的细节",
                    "若同一细节有多种说法，可以一并列出",
                  ].map((tip) => (
                    <li key={tip} className="flex items-start gap-2.5">
                      <i
                        className="ri-check-line text-accent-400 mt-1 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-sm leading-relaxed text-secondary-200/90">
                        {tip}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}